import React, {useMemo, useState} from 'react';
import {Dialog} from '@common/ui/overlays/dialog/dialog';
import {DialogHeader} from '@common/ui/overlays/dialog/dialog-header';
import {DialogBody} from '@common/ui/overlays/dialog/dialog-body';
import {DialogFooter} from '@common/ui/overlays/dialog/dialog-footer';
import {Button} from '@common/ui/buttons/button';
import {useDialogContext} from '@common/ui/overlays/dialog/dialog-context';
import {openDialog} from '@common/ui/overlays/store/dialog-store';
import {PaymentsIcon} from '@common/icons/material/Payments';
import {CreditCardIcon} from '@common/icons/material/CreditCard';
import {showHttpErrorToast} from '@common/utils/http/show-http-error-toast';
import {useAccountUsage} from '@app/editor/use-account-usage';
import {useCreateAiTopupCheckout} from '@app/editor/ai/usage/use-ai-usage-topups';
import {useAuth} from '@common/auth/use-auth';
import './buy-ai-usage-dialog.css';

const FALLBACK_PACKS = [
  {
    key: 'usd_50',
    label: '$50',
    credits_usd: 50,
    discount_percent: 10,
    pay_usd: 45,
  },
  {
    key: 'usd_250',
    label: '$250',
    credits_usd: 250,
    discount_percent: 20,
    pay_usd: 200,
  },
  {
    key: 'usd_1000',
    label: '$1,000',
    credits_usd: 1000,
    discount_percent: 30,
    pay_usd: 700,
  },
];

interface BuyAiUsageDialogProps {
  returnPath?: string;
}

export function openBuyAiUsageDialog(props: BuyAiUsageDialogProps = {}) {
  return openDialog(BuyAiUsageDialog, props);
}

export function BuyAiUsageDialog({returnPath}: BuyAiUsageDialogProps) {
  const {close} = useDialogContext();
  const {user} = useAuth();
  const {data: usage} = useAccountUsage();
  const createCheckout = useCreateAiTopupCheckout();

  const topups = usage?.ai.topups;
  const packs = topups?.packs?.length ? topups.packs : FALLBACK_PACKS;
  const enabled = topups?.enabled ?? true;

  const [packKey, setPackKey] = useState<string>(packs[0]?.key ?? 'usd_50');
  const [customAmount, setCustomAmount] = useState<number>(100);

  const activePack = packs.find(pack => pack.key === packKey) ?? null;
  const isCustom = packKey === 'custom';

  const creditAmount = isCustom
    ? Math.max(0, Number(customAmount || 0))
    : activePack?.credits_usd ?? 0;
  const discountPercent = isCustom ? 0 : activePack?.discount_percent ?? 0;
  const discountAmount = roundCurrency((creditAmount * discountPercent) / 100);
  const totalDue = roundCurrency(creditAmount - discountAmount);

  const validCustomAmount = !isCustom || (creditAmount >= 20 && creditAmount <= 5000);
  const canSubmit = enabled && creditAmount > 0 && validCustomAmount;

  const paymentMethodLabel = useMemo(() => {
    if (user?.card_brand && user?.card_last_four) {
      return `${capitalize(user.card_brand)} •••• ${user.card_last_four}`;
    }
    return 'Card details are collected securely on Stripe checkout.';
  }, [user?.card_brand, user?.card_last_four]);

  return (
    <Dialog size="lg">
      <DialogHeader>
        <div className="aiTopupDialog__head">
          <span className="aiTopupDialog__headIcon">
            <PaymentsIcon />
          </span>
          <div>
            <div className="aiTopupDialog__title">Buy extra usage</div>
            <p className="aiTopupDialog__subtitle">
              Choose an amount to add monthly AI credits instantly.
            </p>
          </div>
        </div>
      </DialogHeader>

      <DialogBody>
        <div className="aiTopupDialog">
          <div className="aiTopupDialog__packs">
            {packs.map(pack => (
              <button
                key={pack.key}
                type="button"
                className={`aiTopupDialog__pack ${
                  pack.key === packKey ? 'aiTopupDialog__pack--active' : ''
                }`}
                onClick={() => setPackKey(pack.key)}
              >
                <div className="aiTopupDialog__packAmount">{pack.label}</div>
                {pack.discount_percent > 0 && (
                  <div className="aiTopupDialog__packDiscount">
                    Save {pack.discount_percent}%
                  </div>
                )}
              </button>
            ))}
            <button
              type="button"
              className={`aiTopupDialog__pack ${
                isCustom ? 'aiTopupDialog__pack--active' : ''
              }`}
              onClick={() => setPackKey('custom')}
            >
              <div className="aiTopupDialog__packAmount">Other</div>
              <div className="aiTopupDialog__packDiscount">Custom</div>
            </button>
          </div>

          {isCustom && (
            <div className="aiTopupDialog__custom">
              <label htmlFor="topup-custom-amount">Amount (USD)</label>
              <input
                id="topup-custom-amount"
                type="number"
                min={20}
                max={5000}
                step={1}
                value={Number.isNaN(customAmount) ? '' : customAmount}
                onChange={e => setCustomAmount(Number(e.target.value || 0))}
              />
              <p>Custom top-up must be between $20 and $5,000.</p>
            </div>
          )}

          <div className="aiTopupDialog__summary">
            <div className="aiTopupDialog__line">
              <span>Extra usage</span>
              <strong>USD {formatMoney(creditAmount)}</strong>
            </div>
            <div className="aiTopupDialog__line">
              <span>Discount</span>
              <strong>
                {discountPercent > 0
                  ? `Save ${discountPercent}% (USD ${formatMoney(discountAmount)})`
                  : 'No discount'}
              </strong>
            </div>
            <div className="aiTopupDialog__line aiTopupDialog__line--total">
              <span>Total due</span>
              <strong>USD {formatMoney(totalDue)}</strong>
            </div>
          </div>

          <div className="aiTopupDialog__payment">
            <span className="aiTopupDialog__paymentLabel">Payment method</span>
            <span className="aiTopupDialog__paymentValue">
              <CreditCardIcon />
              {paymentMethodLabel}
            </span>
          </div>

          {!enabled && (
            <div className="aiTopupDialog__error">
              Extra AI usage checkout is currently unavailable.
            </div>
          )}
        </div>
      </DialogBody>

      <DialogFooter>
        <Button onClick={() => close()}>Cancel</Button>
        <Button
          variant="flat"
          color="primary"
          disabled={!canSubmit || createCheckout.isPending}
          onClick={async () => {
            try {
              const response = await createCheckout.mutateAsync({
                pack: isCustom ? 'custom' : packKey,
                custom_amount: isCustom ? creditAmount : undefined,
                return_path:
                  returnPath ??
                  `${window.location.pathname}${window.location.search}`,
              });
              if (response.checkout_url) {
                window.location.assign(response.checkout_url);
              } else {
                close();
              }
            } catch (err) {
              showHttpErrorToast(err);
            }
          }}
        >
          {createCheckout.isPending
            ? 'Redirecting...'
            : `Pay USD ${formatMoney(totalDue)} now`}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

function formatMoney(value: number): string {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function roundCurrency(value: number): number {
  return Math.round(value * 100) / 100;
}

function capitalize(value: string): string {
  if (!value) return value;
  return `${value[0].toUpperCase()}${value.slice(1)}`;
}
